import {
    DeleteOutlined,
    SaveOutlined,
    SendOutlined,
    ApartmentOutlined,
    TagOutlined,
    DownOutlined,
    RightOutlined,
} from '@ant-design/icons';
import {
    Button, Col, DatePicker, Form, Input, InputNumber, Row, Select,
    Space, Popconfirm, Checkbox, Tag, Badge, Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { responseStatus } from '../../constants';
import { Links } from '../../helpers/Links';
import { dateCommon, validateMessages } from '../../helpers/common';
import {
    BienBanBanGiao,
    BienBanBanGiaoRequest,
    ChiTietBanGiaoRequest,
    TrangThaiBienBanBanGiao,
    ChiTietBienBanBanGiaoModel,
} from '../../models/taisan/BienBanBanGiao';
import { TaiSan } from '../../models/taisan/TaiSan';
import { luuBienBan, guiYeuCauBanGiao, layDanhSachTaiSanCoTheBanGiao } from '../../services/bienBanBanGiao';
import DonViSelectTree from '../utils/DonViSelectTree';
import NhanVienSelectBox from '../utils/NhanVienSelectBox';
import LanhDaoPhongBanSelectBox from '../utils/LanhDaoPhongBanSelectBox';
import viVN from 'antd/lib/date-picker/locale/vi_VN';

const { TextArea } = Input;
const { Text } = Typography;

// Kiểu nhóm tài sản
interface NhomTaiSan {
    chinh: TaiSan;
    dsPhu: TaiSan[];
}

interface ChonGroup {
    chinh: ChiTietBanGiaoRequest;
    dsPhu: ChiTietBanGiaoRequest[];
}

/** Phân nhóm: tài sản chính (có tài sản phụ), riêng lẻ (không có tài sản phụ và không phải phụ) */
function phanNhomTaiSan(ds: TaiSan[]): { nhomChinh: NhomTaiSan[]; dsRiengLe: TaiSan[] } {
    const mapChinh = new Map<number, TaiSan>();
    const mapPhu = new Map<number, TaiSan[]>();

    ds.forEach(ts => {
        if (!ts.idTaiSanChinh) {
            mapChinh.set(ts.id!, ts);
        }
    });

    ds.forEach(ts => {
        if (ts.idTaiSanChinh && mapChinh.has(ts.idTaiSanChinh)) {
            const list = mapPhu.get(ts.idTaiSanChinh) ?? [];
            list.push(ts);
            mapPhu.set(ts.idTaiSanChinh, list);
        }
    });

    const nhomChinh: NhomTaiSan[] = [];
    const dsRiengLe: TaiSan[] = [];

    ds.forEach(ts => {
        if (ts.idTaiSanChinh) return;
        const dsPhu = mapPhu.get(ts.id!) ?? [];
        if (dsPhu.length > 0) {
            nhomChinh.push({ chinh: ts, dsPhu });
        } else {
            // tài sản không có tài sản phụ → riêng lẻ
            dsRiengLe.push(ts);
        }
    });

    // Thêm các tài sản phụ của tài sản chính không nằm trong danh sách (phu_rieng)
    ds.forEach(ts => {
        if (ts.idTaiSanChinh && !mapChinh.has(ts.idTaiSanChinh)) {
            dsRiengLe.push(ts);
        }
    });

    return { nhomChinh, dsRiengLe };
}

// Helper: tạo ChiTietBanGiaoRequest từ TaiSan
function taoChiTietBanGiao(ts: TaiSan, idDonViNhan: number): ChiTietBanGiaoRequest {
    return {
        idTaiSan: ts.id!,
        soLuong: 1,
        tinhTrangTaiSan: 'Tốt',
        idNVSuDungSauBanGiao: undefined,
        idDonViSuDung: idDonViNhan,
        isDonViChinh: !ts.idTaiSanChinh,
        ghiChu: '',
    };
}

const LuuBienBanBanGiaoBox = ({ modelProps, onSubmit }: {
    modelProps: BienBanBanGiao | null;
    onSubmit: () => void;
}) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();

    const [nhomChinh, setNhomChinh] = useState<NhomTaiSan[]>([]);
    const [dsRiengLe, setDsRiengLe] = useState<TaiSan[]>([]);
    const [dsTaiSanChon, setDsTaiSanChon] = useState<ChiTietBanGiaoRequest[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    const [displayMap, setDisplayMap] = useState<Map<number, { ma: string; tenTaiSan: string }>>(new Map());

    const [idDonViGiao, setIdDonViGiao] = useState<number | undefined>();
    const [idDonViNhan, setIdDonViNhan] = useState<number | undefined>();
    const [dsNguoiDaiDienGiao, setDsNguoiDaiDienGiao] = useState<number[]>([]);
    const [dsNguoiDaiDienNhan, setDsNguoiDaiDienNhan] = useState<number[]>([]);

    // ── Load chi tiết khi sửa ──
    const setFormDetail = (value: BienBanBanGiao | null) => {
        form.setFieldsValue({
            ngayLap: value?.ngayLap ? dayjs(dateCommon.formatDate(value.ngayLap), 'DD/MM/YYYY') : dayjs(),
            idDonViGiao: value?.idDonViGiao,
            idDonViNhan: value?.idDonViNhan,
            noiDungBanGiao: value?.noiDungBanGiao,
            diaDiem: value?.diaDiem,
        });
        setIdDonViGiao(value?.idDonViGiao);
        setIdDonViNhan(value?.idDonViNhan);

        if (value?.danhSachNguoiDaiDienGiao)
            setDsNguoiDaiDienGiao(value.danhSachNguoiDaiDienGiao.map((x: any) => x.idNhanVien));
        if (value?.danhSachNguoiDaiDienNhan)
            setDsNguoiDaiDienNhan(value.danhSachNguoiDaiDienNhan.map((x: any) => x.idNhanVien));

        if (value?.id) {
            const ct = value as ChiTietBienBanBanGiaoModel;
            if (ct.danhSachTaiSan?.length) {
                const items: ChiTietBanGiaoRequest[] = ct.danhSachTaiSan.map((item: any) => ({
                    id: item.id,
                    idTaiSan: item.idTaiSan,
                    soLuong: item.soLuong,
                    tinhTrangTaiSan: item.tinhTrangTaiSan || 'Tốt',
                    idNVSuDungSauBanGiao: item.idNVSuDungSauBanGiao,
                    idDonViSuDung: item.idDonViSuDung,
                    isDonViChinh: item.isDonViChinh,
                    ghiChu: item.ghiChu || '',
                }));
                setDsTaiSanChon(items);
                setSelectedIds(new Set(items.map(i => i.idTaiSan)));

                const map = new Map<number, { ma: string; tenTaiSan: string }>();
                ct.danhSachTaiSan.forEach((item: any) =>
                    map.set(item.idTaiSan, { ma: item.maTaiSan || '', tenTaiSan: item.tenTaiSan || '' })
                );
                setDisplayMap(map);
            }
        }
    };

    // ── Load danh sách tài sản theo đơn vị ──
    const loadDanhSachTaiSan = async (idDonVi: number) => {
        try {
            setIsLoading(true);
            const result: TaiSan[] = await layDanhSachTaiSanCoTheBanGiao(idDonVi);
            const { nhomChinh: nc, dsRiengLe: rl } = phanNhomTaiSan(result);
            setNhomChinh(nc);
            setDsRiengLe(rl);
        } catch (err: any) {
            toast.error('Lỗi khi tải danh sách tài sản: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const addTaiSanPure = (
        ts: TaiSan,
        ids: Set<number>,
        list: ChiTietBanGiaoRequest[],
        newDisplayMap: Map<number, { ma: string; tenTaiSan: string }>
    ): { ids: Set<number>; list: ChiTietBanGiaoRequest[] } => {
        if (ids.has(ts.id!)) return { ids, list };
        const newIds = new Set(ids);
        newIds.add(ts.id!);
        const idDV = form.getFieldValue('idDonViNhan') || 0;
        newDisplayMap.set(ts.id!, { ma: ts.ma || '', tenTaiSan: ts.tenTaiSan || '' });
        return { ids: newIds, list: [...list, taoChiTietBanGiao(ts, idDV)] };
    };

    const removeTaiSanPure = (
        id: number,
        ids: Set<number>,
        list: ChiTietBanGiaoRequest[]
    ): { ids: Set<number>; list: ChiTietBanGiaoRequest[] } => {
        const newIds = new Set(ids);
        newIds.delete(id);
        return { ids: newIds, list: list.filter(x => x.idTaiSan !== id) };
    };

    const handleChonChinh = (nhom: NhomTaiSan, checked: boolean) => {
        const newMap = new Map(displayMap);
        let ids = new Set(selectedIds);
        let list = [...dsTaiSanChon];
        if (checked) {
            ({ ids, list } = addTaiSanPure(nhom.chinh, ids, list, newMap));
            nhom.dsPhu.forEach(phu => {
                ({ ids, list } = addTaiSanPure(phu, ids, list, newMap));
            });
        } else {
            ({ ids, list } = removeTaiSanPure(nhom.chinh.id!, ids, list));
            nhom.dsPhu.forEach(phu => {
                ({ ids, list } = removeTaiSanPure(phu.id!, ids, list));
            });
        }
        setSelectedIds(ids);
        setDsTaiSanChon(list);
        setDisplayMap(newMap);
    };

    const handleChonPhu = (phu: TaiSan, checked: boolean) => {
        const newMap = new Map(displayMap);
        let ids = new Set(selectedIds);
        let list = [...dsTaiSanChon];
        if (checked) {
            ({ ids, list } = addTaiSanPure(phu, ids, list, newMap));
        } else {
            ({ ids, list } = removeTaiSanPure(phu.id!, ids, list));
        }
        setSelectedIds(ids);
        setDsTaiSanChon(list);
        setDisplayMap(newMap);
    };

    const handleChonRiengLe = (ts: TaiSan, checked: boolean) => {
        const newMap = new Map(displayMap);
        let ids = new Set(selectedIds);
        let list = [...dsTaiSanChon];
        if (checked) {
            ({ ids, list } = addTaiSanPure(ts, ids, list, newMap));
        } else {
            ({ ids, list } = removeTaiSanPure(ts.id!, ids, list));
        }
        setSelectedIds(ids);
        setDsTaiSanChon(list);
        setDisplayMap(newMap);
    };

    const getChinhState = (nhom: NhomTaiSan): { checked: boolean; indeterminate: boolean } => {
        const chinhChon = selectedIds.has(nhom.chinh.id!);
        const soLuongPhuChon = nhom.dsPhu.filter(p => selectedIds.has(p.id!)).length;
        if (chinhChon) return { checked: true, indeterminate: false };
        if (soLuongPhuChon > 0) return { checked: false, indeterminate: true };
        return { checked: false, indeterminate: false };
    };

    const handleCapNhat = (idTaiSan: number, field: string, value: any) =>
        setDsTaiSanChon(prev =>
            prev.map(item => item.idTaiSan === idTaiSan ? { ...item, [field]: value } : item)
        );

    const handleXoa = (idTaiSan: number) => {
        setDsTaiSanChon(prev => prev.filter(x => x.idTaiSan !== idTaiSan));
        setSelectedIds(prev => { const s = new Set(prev); s.delete(idTaiSan); return s; });
    };

    // ── Submit ──
    const onFinish = async (values: any, guiYeuCau = false) => {
        if (!dsTaiSanChon.length) { toast.warning('Vui lòng chọn ít nhất một tài sản'); return; }
        if (!dsNguoiDaiDienGiao.length) { toast.warning('Vui lòng chọn người đại diện bên giao'); return; }
        if (!dsNguoiDaiDienNhan.length) { toast.warning('Vui lòng chọn người đại diện bên nhận'); return; }

        setIsLoading(true);
        const request: BienBanBanGiaoRequest = {
            id: modelProps?.id || undefined,
            idDonViGiao: values.idDonViGiao,
            idDonViNhan: values.idDonViNhan,
            noiDungBanGiao: values.noiDungBanGiao,
            diaDiem: values.diaDiem,
            trangThai: TrangThaiBienBanBanGiao.DuThao,
            danhSachNguoiDaiDienGiao: dsNguoiDaiDienGiao,
            danhSachNguoiDaiDienNhan: dsNguoiDaiDienNhan,
            danhSachTaiSan: dsTaiSanChon,
            ...values
        };

        try {
            const res = await luuBienBan(request);
            if (res.trangThai === responseStatus.thanhCong) {
                toast.success(res.thongBao);
                if (guiYeuCau) {
                    try {
                        await guiYeuCauBanGiao(res.duLieu);
                        toast.success('Gửi yêu cầu xác nhận thành công');
                    } catch (err: any) {
                        toast.error('Lỗi khi gửi yêu cầu: ' + err.message);
                    }
                }
                onSubmit();
            } else {
                toast.error(res.thongBao);
            }
        } catch (err: any) {
            toast.error('Lỗi khi lưu biên bản: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { setFormDetail(modelProps); }, [modelProps]);
    useEffect(() => { if (idDonViGiao) loadDanhSachTaiSan(idDonViGiao); }, [idDonViGiao]);

    // Render tài sản khả dụng

    const toggleExpand = (id: number) => {
        setExpandedGroups(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const renderRowChinh = (nhom: NhomTaiSan) => {
        const { checked, indeterminate } = getChinhState(nhom);
        const expanded = expandedGroups.has(nhom.chinh.id!);

        return (
            <div key={nhom.chinh.id} style={{ marginBottom: 4 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px',
                    background: checked || indeterminate ? '#e6f4ff' : '#fafafa',
                    border: `1px solid ${checked || indeterminate ? '#91caff' : '#e8e8e8'}`,
                    borderRadius: expanded ? '6px 6px 0 0' : 6,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                }}>
                    <Button
                        type="text" size="small"
                        icon={expanded ? <DownOutlined /> : <RightOutlined />}
                        onClick={() => toggleExpand(nhom.chinh.id!)}
                        style={{ color: '#1677ff', flexShrink: 0 }}
                    />
                    <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={e => handleChonChinh(nhom, e.target.checked)}
                        onClick={e => e.stopPropagation()}
                    />
                    <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggleExpand(nhom.chinh.id!)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tag color="blue" icon={<ApartmentOutlined />} style={{ margin: 0 }}>Tài sản chính</Tag>
                            <Text strong style={{ fontSize: 14 }}>{nhom.chinh.tenTaiSan}</Text>
                        </div>
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                            {nhom.chinh.ma}
                            {nhom.chinh.nguoiSuDung && (
                                <span style={{ marginLeft: 12 }}>👤 {nhom.chinh.nguoiSuDung}</span>
                            )}
                        </div>
                    </div>
                    <Badge count={nhom.dsPhu.length} color="#1677ff"
                        title={`${nhom.dsPhu.length} tài sản phụ`}
                        style={{ flexShrink: 0 }} />
                </div>

                {expanded && (
                    <div style={{
                        border: '1px solid #91caff',
                        borderTop: 'none',
                        borderRadius: '0 0 6px 6px',
                        background: '#fff',
                        overflow: 'hidden',
                    }}>
                        {nhom.dsPhu.map((phu, idx) => {
                            const phuChon = selectedIds.has(phu.id!);
                            return (
                                <div key={phu.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '7px 12px 7px 44px',
                                    background: phuChon ? '#f0f7ff' : '#fff',
                                    borderTop: idx > 0 ? '1px solid #f0f0f0' : undefined,
                                    transition: 'background 0.15s',
                                }}>
                                    <Checkbox
                                        checked={phuChon}
                                        onChange={e => handleChonPhu(phu, e.target.checked)}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>Phụ</Tag>
                                            <Text style={{ fontSize: 13 }}>{phu.tenTaiSan}</Text>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 1 }}>
                                            {phu.ma}
                                            {phu.nguoiSuDung && (
                                                <span style={{ marginLeft: 12 }}>👤 {phu.nguoiSuDung}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderRowRiengLe = (ts: TaiSan) => {
        const chon = selectedIds.has(ts.id!);
        return (
            <div key={ts.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px',
                background: chon ? '#fff7e6' : '#fafafa',
                border: `1px solid ${chon ? '#ffc069' : '#e8e8e8'}`,
                borderRadius: 6,
                marginBottom: 4,
                transition: 'all 0.2s',
            }}>
                <div style={{ width: 28, flexShrink: 0 }} />
                <Checkbox
                    checked={chon}
                    onChange={e => handleChonRiengLe(ts, e.target.checked)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Tag color="orange" icon={<TagOutlined />} style={{ margin: 0 }}>
                            {ts.idTaiSanChinh ? 'Phụ riêng lẻ' : 'Tài sản'}
                        </Tag>
                        <Text style={{ fontSize: 13 }}>{ts.tenTaiSan}</Text>
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                        {ts.ma}
                        {ts.nguoiSuDung && (
                            <span style={{ marginLeft: 12 }}>👤 {ts.nguoiSuDung}</span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render danh sách tài sản đã chọn (theo cấp)

    const buildChonGroups = (): { nhomChon: ChonGroup[]; riengLeChon: ChiTietBanGiaoRequest[] } => {
        // Map idTaiSanChinh → nhom
        const nhomMap = new Map<number, ChonGroup>();
        const riengLeChon: ChiTietBanGiaoRequest[] = [];

        // Xác định tài sản chính trong dsTaiSanChon
        nhomChinh.forEach(nhom => {
            if (selectedIds.has(nhom.chinh.id!)) {
                nhomMap.set(nhom.chinh.id!, { chinh: dsTaiSanChon.find(x => x.idTaiSan === nhom.chinh.id!)!, dsPhu: [] });
            }
        });

        dsTaiSanChon.forEach(item => {
            const nhom = nhomChinh.find(n => n.dsPhu.some(p => p.id === item.idTaiSan));
            if (nhom) {
                const group = nhomMap.get(nhom.chinh.id!);
                if (group) {
                    group.dsPhu.push(item);
                } else {
                    const orphan = nhomMap.get(-(nhom.chinh.id!));
                    if (orphan) {
                        orphan.dsPhu.push(item);
                    } else {
                        nhomMap.set(-(nhom.chinh.id!), {
                            chinh: {
                                idTaiSan: -(nhom.chinh.id!),
                                soLuong: 0,
                                tinhTrangTaiSan: '',
                                idDonViSuDung: 0,
                                isDonViChinh: true,
                                ghiChu: '',
                            } as any,
                            dsPhu: [item],
                        });
                    }
                }
            } else if (!nhomChinh.some(n => n.chinh.id === item.idTaiSan)) {
                riengLeChon.push(item);
            }
        });

        return {
            nhomChon: Array.from(nhomMap.values()),
            riengLeChon,
        };
    };

    const getTenTaiSan = (idTaiSan: number): { ten: string; ma: string } => {
        const info = displayMap.get(idTaiSan);
        for (const nhom of nhomChinh) {
            if (nhom.chinh.id === idTaiSan) return { ten: nhom.chinh.tenTaiSan, ma: nhom.chinh.ma };
            const phu = nhom.dsPhu.find(p => p.id === idTaiSan);
            if (phu) return { ten: phu.tenTaiSan, ma: phu.ma };
        }
        const rl = dsRiengLe.find(t => t.id === idTaiSan);
        if (rl) return { ten: rl.tenTaiSan, ma: rl.ma };
        return { ten: info?.tenTaiSan || `#${idTaiSan}`, ma: info?.ma || '' };
    };

    const renderInlineEdit = (item: ChiTietBanGiaoRequest) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
            <InputNumber
                min={1} value={item.soLuong}
                style={{ width: 102 }}
                size="small"
                addonBefore="SL"
                onChange={val => handleCapNhat(item.idTaiSan, 'soLuong', val)}
            />
            <Select
                value={item.tinhTrangTaiSan} size="small"
                style={{ width: 110 }}
                onChange={val => handleCapNhat(item.idTaiSan, 'tinhTrangTaiSan', val)}
            >
                {['Tốt', 'Khá', 'Trung bình', 'Kém'].map(x => (
                    <Select.Option key={x} value={x}>{x}</Select.Option>
                ))}
            </Select>
            <div style={{width: "35%"}}>
                <NhanVienSelectBox
                    value={item.idNVSuDungSauBanGiao}
                    allowClear
                    onChange={(val: any) => handleCapNhat(item.idTaiSan, 'idNVSuDungSauBanGiao', val?.target?.value ?? val)}
                />
            </div>
            <Input
                value={item.ghiChu} placeholder="Ghi chú" size="small"
                style={{ width: 540 }}
                onChange={e => handleCapNhat(item.idTaiSan, 'ghiChu', e.target.value)}
            />
            <Popconfirm title="Xoá tài sản này?" okText="Đồng ý" cancelText="Huỷ"
                onConfirm={() => handleXoa(item.idTaiSan)}>
                <Button type="link" danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
        </div>
    );

    const renderDaTaiSanChon = () => {
        if (!dsTaiSanChon.length)
            return <div style={{ color: '#bbb', padding: '16px 0', textAlign: 'center' }}>Chưa có tài sản nào được chọn</div>;

        const { nhomChon, riengLeChon } = buildChonGroups();

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nhomChon.map(group => {
                    const isOrphan = group.chinh.idTaiSan < 0;
                    const realChinhId = isOrphan ? -group.chinh.idTaiSan : group.chinh.idTaiSan;
                    const chinhInfo = isOrphan
                        ? { ten: nhomChinh.find(n => n.chinh.id === realChinhId)?.chinh.tenTaiSan || '', ma: nhomChinh.find(n => n.chinh.id === realChinhId)?.chinh.ma || '' }
                        : getTenTaiSan(group.chinh.idTaiSan);

                    return (
                        <div key={group.chinh.idTaiSan} style={{
                            border: '1px solid #91caff',
                            borderRadius: 6,
                            overflow: 'hidden',
                        }}>
                            {!isOrphan && (
                                <div style={{
                                    background: '#e6f4ff',
                                    padding: '8px 12px',
                                    borderBottom: group.dsPhu.length ? '1px solid #bae0ff' : 'none',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                        <Tag color="blue" icon={<ApartmentOutlined />} style={{ margin: 0, flexShrink: 0, marginTop: 2 }}>Chính</Tag>
                                        <div style={{ flex: 1 }}>
                                            <Text strong>{chinhInfo.ten}</Text>
                                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{chinhInfo.ma}</Text>
                                            {renderInlineEdit(group.chinh)}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isOrphan && (
                                <div style={{
                                    background: '#f5f5f5',
                                    padding: '6px 12px',
                                    borderBottom: '1px solid #e8e8e8',
                                }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <ApartmentOutlined style={{ marginRight: 4 }} />
                                        Tài sản phụ của: <em>{chinhInfo.ten}</em>
                                    </Text>
                                </div>
                            )}
                            {group.dsPhu.map((phu, idx) => {
                                const phuInfo = getTenTaiSan(phu.idTaiSan);
                                return (
                                    <div key={phu.idTaiSan} style={{
                                        padding: '8px 12px 8px 32px',
                                        background: '#fff',
                                        borderTop: idx > 0 ? '1px solid #f0f0f0' : undefined,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                            <Tag color="cyan" style={{ margin: 0, flexShrink: 0, marginTop: 2, fontSize: 11 }}>Phụ</Tag>
                                            <div style={{ flex: 1 }}>
                                                <Text>{phuInfo.ten}</Text>
                                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{phuInfo.ma}</Text>
                                                {renderInlineEdit(phu)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {riengLeChon.map(item => {
                    const info = getTenTaiSan(item.idTaiSan);
                    return (
                        <div key={item.idTaiSan} style={{
                            border: '1px solid #ffd591',
                            borderRadius: 6,
                            padding: '8px 12px',
                            background: '#fff7e6',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <Tag color="orange" icon={<TagOutlined />} style={{ margin: 0, flexShrink: 0, marginTop: 2 }}>
                                    {dsRiengLe.find(t => t.id === item.idTaiSan)?.idTaiSanChinh ? 'Phụ' : 'Tài sản'}
                                </Tag>
                                <div style={{ flex: 1 }}>
                                    <Text>{info.ten}</Text>
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{info.ma}</Text>
                                    {renderInlineEdit(item)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Thống kê
    const coTaiSan = nhomChinh.length > 0 || dsRiengLe.length > 0;

    return (
        <Form form={form} layout="vertical"
            onFinish={v => onFinish(v, false)}
            validateMessages={validateMessages}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Form.Item name="idDonViGiao" label="Đơn vị giao" rules={[{ required: true }]}>
                        <DonViSelectTree
                            autoSelectFirst={false}
                            onChange={(v: any) => {
                                setIdDonViGiao(v?.target?.value ?? v);
                                setDsTaiSanChon([]);
                                setSelectedIds(new Set());
                                setExpandedGroups(new Set());
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name="idDonViNhan" label="Đơn vị nhận" rules={[{ required: true }]}>
                        <DonViSelectTree
                            autoSelectFirst={false}
                            onChange={(v: any) => {
                                setIdDonViNhan(v?.target?.value ?? v);
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name="ngayLap" label="Ngày lập" rules={[{ required: true }]}>
                        <DatePicker format={["DD/MM/YYYY", "DDMMYYYY"]} locale={viVN} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Người đại diện bên giao" rules={[{ required: true }]}>
                        <LanhDaoPhongBanSelectBox
                            multiple
                            value={dsNguoiDaiDienGiao as any}
                            idDonVi={idDonViGiao}
                            onChange={(v: any) => setDsNguoiDaiDienGiao(v?.target?.value ?? v)} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Người đại diện bên nhận" rules={[{ required: true }]}>
                        <LanhDaoPhongBanSelectBox
                            multiple
                            idDonVi={idDonViNhan}
                            value={dsNguoiDaiDienNhan as any}
                            onChange={(v: any) => setDsNguoiDaiDienNhan(v?.target?.value ?? v)} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="noiDungBanGiao" label="Nội dung bàn giao">
                        <TextArea rows={3} placeholder="Nhập nội dung bàn giao" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="diaDiem" label="Địa điểm">
                        <Input placeholder="Nhập địa điểm" />
                    </Form.Item>
                </Col>
            </Row>

            <div style={{ display: 'flex', gap: 16, marginTop: 24, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        marginBottom: 6,
                        padding: '10px 14px',
                        background: 'linear-gradient(90deg, #e6f4ff 0%, #f0f7ff 100%)',
                        borderRadius: 8,
                        borderLeft: '4px solid #1677ff',
                    }}>
                        <ApartmentOutlined style={{ fontSize: 18, color: '#1677ff' }} />
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1677ff', flex: 1 }}>Tài sản khả dụng</span>
                        {coTaiSan && (
                            <Space size={4}>
                                {nhomChinh.length > 0 && (
                                    <Tag color="blue" icon={<ApartmentOutlined />}>{nhomChinh.length} nhóm</Tag>
                                )}
                                {dsRiengLe.length > 0 && (
                                    <Tag color="orange" icon={<TagOutlined />}>{dsRiengLe.length} riêng lẻ</Tag>
                                )}
                            </Space>
                        )}
                    </div>
                    <div style={{
                        display: 'flex', gap: 10, marginBottom: 8,
                        padding: '4px 10px', background: '#fafafa',
                        border: '1px solid #f0f0f0', borderRadius: 6, flexWrap: 'wrap',
                        fontSize: 11, minHeight: 30,
                    }}>
                        <Space size={3}>
                            <Tag color="blue" icon={<ApartmentOutlined />} style={{ margin: 0 }} />
                            <span>Chính → chọn toàn bộ phụ</span>
                        </Space>
                        <Space size={3}>
                            <Tag color="cyan" style={{ margin: 0 }}>Phụ</Tag>
                            <span>Chọn độc lập</span>
                        </Space>
                        <Space size={3}>
                            <Tag color="orange" icon={<TagOutlined />} style={{ margin: 0 }} />
                            <span>Riêng lẻ</span>
                        </Space>
                    </div>
                    <div style={{
                        height: 480,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid #e8e8e8',
                        borderRadius: 8,
                        padding: '10px',
                        background: '#fafafa',
                    }}>
                        {isLoading ? (
                            <div style={{ textAlign: 'center', paddingTop: 80, color: '#8c8c8c' }}>Đang tải...</div>
                        ) : !idDonViGiao ? (
                            <div style={{ textAlign: 'center', paddingTop: 80, color: '#bbb' }}>
                                Chọn đơn vị giao để xem danh sách tài sản
                            </div>
                        ) : !coTaiSan ? (
                            <div style={{ textAlign: 'center', paddingTop: 80, color: '#bbb' }}>
                                Không có tài sản khả dụng
                            </div>
                        ) : (
                            <>
                                {nhomChinh.length > 0 && (
                                    <div style={{ marginBottom: 14 }}>
                                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: '#1677ff' }}>
                                            <ApartmentOutlined style={{ marginRight: 5 }} />
                                            Tài sản có tài sản phụ ({nhomChinh.length})
                                        </div>
                                        {nhomChinh.map(renderRowChinh)}
                                    </div>
                                )}
                                {dsRiengLe.length > 0 && (
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: '#d46b08' }}>
                                            <TagOutlined style={{ marginRight: 5 }} />
                                            Tài sản riêng lẻ ({dsRiengLe.length})
                                        </div>
                                        {dsRiengLe.map(renderRowRiengLe)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        marginBottom: 6,
                        padding: '10px 14px',
                        background: 'linear-gradient(90deg, #f6ffed 0%, #f0fff4 100%)',
                        borderRadius: 8,
                        borderLeft: '4px solid #52c41a',
                    }}>
                        <TagOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#389e0d', flex: 1 }}>Tài sản bàn giao</span>
                        {dsTaiSanChon.length > 0 && (
                            <Badge count={dsTaiSanChon.length} style={{ backgroundColor: '#52c41a' }} />
                        )}
                    </div>

                    <div style={{ height: 38, marginBottom: 8 }} />
                    <div style={{
                        height: 480,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid #e8e8e8',
                        borderRadius: 8,
                        padding: '10px',
                        background: '#fff',
                    }}>
                        {renderDaTaiSanChon()}
                    </div>
                </div>
            </div>

            <Row style={{ marginTop: 24 }}>
                <Col xs={24}>
                    <Space wrap>
                        <Button onClick={() => navigate(Links.BienBanBanGiao.DanhSach)}>
                            Huỷ
                        </Button>
                        <Button type="primary" htmlType="submit"
                            icon={<SaveOutlined />} loading={isLoading}>
                            Lưu tạm
                        </Button>
                        <Button type="primary"
                            icon={<SendOutlined />}
                            loading={isLoading}
                            onClick={() => form.validateFields().then(v => onFinish(v, true))}>
                            Lưu và gửi yêu cầu xác nhận
                        </Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};

export default LuuBienBanBanGiaoBox;